import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';

/* eslint-disable @typescript-eslint/no-explicit-any */

/** What the visitor allowed. Necessary cookies are not listed: they are not optional. */
export interface ConsentChoice {
  analytics: boolean;
  advertising: boolean;
}

const STORAGE_KEY = 'sav-cookie-consent';

/** Bumped if the categories ever change, which makes every stored choice stale and re-asks. */
const CONSENT_VERSION = 1;

interface StoredConsent extends ConsentChoice {
  version: number;
  at: string;
}

/**
 * Cookie consent, wired to Google Consent Mode.
 *
 * Consent Mode rather than blocking the Google scripts: with `denied` defaults, gtag and AdSense
 * still load but hold everything back — no analytics cookie, no advertising identifiers, and pings
 * sent without them. Blocking the scripts outright would achieve the same privacy result and break
 * more, because AdSense is loaded from index.html and cannot be conditionally removed from there.
 *
 * The defaults have to be pushed BEFORE gtag is configured. A `consent default` arriving after the
 * config call is too late — the tag has already decided what it may store. AnalyticsService calls
 * `applyDefaults()` first for that reason.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);

  private readonly choice = signal<ConsentChoice | null>(null);

  /** True while the visitor has not answered — which is what the banner watches. */
  readonly needsChoice = computed(() => this.choice() === null);
  readonly current = computed(() => this.choice());

  private get gtag(): ((...args: any[]) => void) | null {
    const fn = (this.document.defaultView as any)?.gtag;
    return typeof fn === 'function' ? fn : null;
  }

  /**
   * Reads any stored choice and pushes the resulting Consent Mode defaults.
   *
   * Called before gtag is configured, so a returning visitor's answer is already in place when the
   * tag initialises and there is no window in which it behaves as if consent were absent.
   */
  applyDefaults(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const stored = this.read();
    if (stored) this.choice.set({ analytics: stored.analytics, advertising: stored.advertising });

    this.push('default', {
      analytics: stored?.analytics ?? false,
      advertising: stored?.advertising ?? false,
    });
  }

  /** Records a choice and tells Google about it immediately. */
  set(choice: ConsentChoice): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.choice.set(choice);
    this.write(choice);
    this.push('update', choice);
  }

  acceptAll(): void {
    this.set({ analytics: true, advertising: true });
  }

  /** Necessary cookies only. Still a recorded answer, so the banner does not ask again. */
  rejectAll(): void {
    this.set({ analytics: false, advertising: false });
  }

  /** Reopens the banner — what the footer's "Cookie settings" link calls. */
  reopen(): void {
    this.choice.set(null);
  }

  private push(mode: 'default' | 'update', choice: ConsentChoice): void {
    const granted = (allowed: boolean) => (allowed ? 'granted' : 'denied');

    this.gtag?.('consent', mode, {
      analytics_storage: granted(choice.analytics),
      ad_storage: granted(choice.advertising),
      ad_user_data: granted(choice.advertising),
      ad_personalization: granted(choice.advertising),
      // Never denied: this is the cookie remembering the answer itself, and denying it would mean
      // asking again on every page.
      functionality_storage: 'granted',
      security_storage: 'granted',
    });
  }

  private read(): StoredConsent | null {
    try {
      const raw = this.document.defaultView?.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw) as StoredConsent;
      // A stored answer to a different set of questions is not an answer to these ones.
      if (parsed?.version !== CONSENT_VERSION) return null;

      return parsed;
    } catch {
      // Private mode, blocked storage, corrupted JSON — treat as unanswered rather than throwing
      // into the page, which would take the banner down with it.
      return null;
    }
  }

  private write(choice: ConsentChoice): void {
    try {
      const payload: StoredConsent = { ...choice, version: CONSENT_VERSION, at: new Date().toISOString() };
      this.document.defaultView?.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Storage unavailable. The choice still applies to this page load; the banner will ask again
      // next visit, which is the correct failure — never assume consent we could not record.
    }
  }
}
